import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { MapPin, Navigation, Search, X } from "lucide-react";

const MapPicker = ({
  onLocationSelect,
  initialLocation = null,
  className = "",
  disabled = false,
}) => {
  const { themeClasses } = useTheme();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [showMap, setShowMap] = useState(false);
  const [mapsAvailable, setMapsAvailable] = useState(true);

  // Initialize Google Maps
  useEffect(() => {
    if (showMap && !map && window.google) {
      initializeMap();
    }
  }, [showMap]);

  const initializeMap = () => {
    const defaultCenter = selectedLocation
      ? { lat: selectedLocation.latitude, lng: selectedLocation.longitude }
      : { lat: 40.7831, lng: -73.9712 }; // Default to NYC

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Add click listener
    mapInstance.addListener("click", (event) => {
      if (!disabled) {
        handleMapClick(event.latLng);
      }
    });

    setMap(mapInstance);

    // Add initial marker if location exists
    if (selectedLocation) {
      addMarker(defaultCenter, mapInstance);
    }
  };

  const addMarker = (position, mapInstance = map) => {
    // Remove existing marker
    if (marker) {
      marker.setMap(null);
    }

    // Add new marker
    const newMarker = new window.google.maps.Marker({
      position: position,
      map: mapInstance,
      title: "Lost dog location",
      icon: {
        url:
          "data:image/svg+xml;charset=UTF-8," +
          encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ef4444"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(24, 24),
      },
    });

    setMarker(newMarker);
  };

  const handleMapClick = (latLng) => {
    const lat = latLng.lat();
    const lng = latLng.lng();

    const locationData = {
      latitude: lat,
      longitude: lng,
      coordinates: `${lat},${lng}`,
      accuracy: null,
      source: "manual",
      timestamp: new Date().toISOString(),
    };

    setSelectedLocation(locationData);
    addMarker({ lat, lng });

    // Reverse geocode to get address
    reverseGeocode(lat, lng, locationData);
  };

  const reverseGeocode = async (lat, lng, locationData) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK") resolve(results);
          else reject(status);
        });
      });

      if (response && response[0]) {
        const address = response[0].formatted_address;
        const enhancedLocation = {
          ...locationData,
          address: address,
          source: "geocoded",
        };
        setSelectedLocation(enhancedLocation);
        onLocationSelect(enhancedLocation);
      } else {
        onLocationSelect(locationData);
      }
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
      onLocationSelect(locationData);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !map) return;

    setIsLoading(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: searchQuery }, (results, status) => {
          if (status === "OK") resolve(results);
          else reject(status);
        });
      });

      if (response && response[0]) {
        const location = response[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        const locationData = {
          latitude: lat,
          longitude: lng,
          coordinates: `${lat},${lng}`,
          address: response[0].formatted_address,
          accuracy: 100, // Estimated accuracy for geocoded results
          source: "geocoded",
          timestamp: new Date().toISOString(),
        };

        setSelectedLocation(locationData);
        addMarker({ lat, lng });
        map.setCenter({ lat, lng });
        map.setZoom(15);
        onLocationSelect(locationData);
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
      alert("Could not find that location. Please try a different address.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = position.coords.accuracy;

        const locationData = {
          latitude: lat,
          longitude: lng,
          coordinates: `${lat},${lng}`,
          accuracy: accuracy,
          source: "gps",
          timestamp: new Date().toISOString(),
        };

        setSelectedLocation(locationData);

        if (map) {
          addMarker({ lat, lng });
          map.setCenter({ lat, lng });
          map.setZoom(16);
        }

        // Reverse geocode to get address
        reverseGeocode(lat, lng, locationData);
        setIsLoading(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert(
          "Could not get your current location. Please select manually on the map."
        );
        setIsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Load Google Maps script
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey || apiKey === "YOUR_GOOGLE_MAPS_API_KEY") {
      console.warn(
        "Google Maps API key not configured. Map functionality will be limited."
      );
      setMapsAvailable(false);
      return;
    }

    if (!window.google && !window.googleMapsLoading) {
      window.googleMapsLoading = true;
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.googleMapsLoading = false;
        if (showMap) {
          initializeMap();
        }
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API");
        window.googleMapsLoading = false;
      };
      document.head.appendChild(script);
    }
  }, []);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Location Display */}
      {selectedLocation && (
        <div
          className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-lg p-4`}
        >
          <div className="flex items-start space-x-3">
            <MapPin className={`w-5 h-5 ${themeClasses.text.primary} mt-0.5`} />
            <div className="flex-1">
              <p className={`${themeClasses.text.primary} font-medium`}>
                Selected Location
              </p>
              {selectedLocation.address && (
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  {selectedLocation.address}
                </p>
              )}
              <p className={`${themeClasses.text.secondary} text-xs mt-1`}>
                {selectedLocation.latitude.toFixed(6)},{" "}
                {selectedLocation.longitude.toFixed(6)}
              </p>
              <div className="flex items-center space-x-4 mt-2 text-xs">
                <span className={`${themeClasses.text.secondary}`}>
                  Source: {selectedLocation.source}
                </span>
                {selectedLocation.accuracy && (
                  <span className={`${themeClasses.text.secondary}`}>
                    Accuracy: ±{Math.round(selectedLocation.accuracy)}m
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedLocation(null);
                setMarker(null);
                onLocationSelect(null);
              }}
              className={`p-1 rounded hover:${themeClasses.bg.hover} ${themeClasses.text.secondary}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Map Toggle Button */}
      {!showMap && (
        <button
          onClick={() => setShowMap(true)}
          disabled={disabled || !mapsAvailable}
          className={`w-full ${themeClasses.bg.button} text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center space-x-2`}
        >
          <MapPin className="w-5 h-5" />
          <span>
            {mapsAvailable ? "Select Location on Map" : "Map Not Available"}
          </span>
        </button>
      )}

      {!mapsAvailable && (
        <div
          className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-lg p-4`}
        >
          <p className={`${themeClasses.text.secondary} text-sm`}>
            🗺️ Interactive map is not available. Please enter the address
            manually in the fields above.
          </p>
        </div>
      )}

      {/* Map Interface */}
      {showMap && (
        <div
          className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-lg overflow-hidden`}
        >
          {/* Map Controls */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex space-x-2 mb-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search for an address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                  disabled={disabled}
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={disabled || isLoading || !searchQuery.trim()}
                className={`px-4 py-2 ${themeClasses.bg.button} text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center space-x-1`}
              >
                <Search className="w-4 h-4" />
              </button>
              <button
                onClick={getCurrentLocation}
                disabled={disabled || isLoading}
                className={`px-4 py-2 border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} rounded-lg transition-colors flex items-center space-x-1`}
              >
                <Navigation className="w-4 h-4" />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <p className={`${themeClasses.text.secondary} text-sm`}>
                Click on the map to select the exact location where your pet was
                last seen
              </p>
              <button
                onClick={() => setShowMap(false)}
                className={`px-3 py-1 text-sm ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
              >
                Hide Map
              </button>
            </div>
          </div>

          {/* Map Container */}
          <div
            ref={mapRef}
            className="w-full h-96 relative"
            style={{ minHeight: "384px" }}
          >
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className={themeClasses.text.primary}>Loading...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPicker;
